import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ChevronDown, Search, X, Wallet } from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, Tabs, Button, Empty, Rows, statusBadge, NumberInput } from '../components/UI'
import { CoinIcon } from '../components/Market'
import { ProChart } from '../components/ProChart'
import { Popover } from '../components/Popover'
import { useMediaQuery } from '../lib/media'
import { useAuth, useMarket, useTickers, toast } from '../store'
import { get, post, del } from '../lib/api'
import { clsx, num, pct, changeClass, fa, faDate, faTime, asArray} from '../lib/format'
import { useIsDesktop } from '../lib/media'
import { LeverageForm, LeveragePositions, useLeverageConfig, leverageOn } from '../components/Leverage'

/* ---------------------------------------------------------------- انتخاب بازار */
function MarketPicker({ symbol }: { symbol: string }) {
  // روی گوشی، فوکوسِ خودکار صفحه‌کلید را بالا می‌آورد و چون منو از پایین
  // صفحه باز می‌شود، فهرست بازارها را می‌پوشاند.
  const coarse = useMediaQuery('(pointer: coarse)')
  const [open, setOpen] = useState(false)
  const [quote, setQuote] = useState('IRT')
  const [q, setQ] = useState('')
  const btn = useRef<HTMLButtonElement>(null)
  const nav = useNavigate()
  const t = useMarket((s) => s.tickers[symbol])
  // اشتراک واکنشی به داده بازار — نه به تابع list که ارجاعش هرگز تغییر نمی‌کند
  const rows = useTickers(quote).filter(
    (r) => !q || r.symbol.toLowerCase().includes(q.toLowerCase()) || r.faName.includes(q),
  )

  return (
    <>
      <button ref={btn} onClick={() => setOpen((o) => !o)} className="flex items-center gap-3 rounded-2xl px-2 py-1.5 transition hover:bg-white/[.05]">
        {t && <CoinIcon symbol={t.base} color={t.color} size={40} />}
        <div className="text-right">
          <div className="flex items-center gap-1.5 font-bold text-white">
            {t?.faName ?? symbol}
            <ChevronDown className="h-4 w-4 text-slate-500" />
          </div>
          <div className="num text-xs text-slate-500" dir="ltr">{t ? `${t.base}/${t.quote === 'IRT' ? 'TMN' : t.quote}` : ''}</div>
        </div>
      </button>

      {/*
        منو از طریق پورتال بیرون از کارت رندر می‌شود.
        پیش از این `absolute z-40` داخل همین کارت بود و **زیر نمودار**
        می‌رفت: کلاس `.card` به‌خاطر `backdrop-blur` یک زمینه چیدمان
        می‌سازد و هرچه داخلش باشد همان‌جا حبس است، هر `z-index`ی که داشته
        باشد. توضیح کامل در `components/Popover.tsx`.
      */}
      <Popover open={open} onClose={() => setOpen(false)} anchor={btn} width={352}>
        <div className="relative mb-3">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input className="input !py-2 pr-9 text-xs" placeholder="جستجوی بازار" value={q}
            onChange={(e) => setQ(e.target.value)} autoFocus={!coarse} />
        </div>
        <Tabs value={quote} onChange={setQuote} size="sm" className="mb-2 w-full"
          items={[{ key: 'IRT', label: 'تومان' }, { key: 'USDT', label: 'تتر' }]} />
        <div className="max-h-80 overflow-y-auto">
          {rows.length === 0 && (
            <div className="py-6 text-center text-xs text-slate-500">بازاری با این نام پیدا نشد</div>
          )}
          {rows.map((r) => (
            <button
              key={r.symbol}
              onClick={() => { setOpen(false); setQ(''); nav(`/trade/${r.symbol}`) }}
              className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-right transition hover:bg-white/[.06]"
            >
              <CoinIcon symbol={r.base} color={r.color} size={30} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-white">{r.faName}</div>
                <div className="num text-[11px] text-slate-500" dir="ltr">{r.symbol}</div>
              </div>
              <div className="text-left">
                <div className="num text-sm text-slate-200">{num(r.last)}</div>
                <div className={clsx('num text-[11px]', changeClass(r.change))}>{pct(r.change)}</div>
              </div>
            </button>
          ))}
        </div>
      </Popover>
    </>
  )
}


function OrderBook({ symbol, onPick }: { symbol: string; onPick: (p: number) => void }) {
  const symbolData = useMarket((s) => s.symbolData)
  const ticker = useMarket((s) => s.tickers[symbol])
  const [book, setBook] = useState<{ bids: number[][]; asks: number[][] }>({ bids: [], asks: [] })

  useEffect(() => {
    let alive = true
    get(`/market/orderbook/${symbol}?limit=14`).then((r: any) => alive && setBook(r)).catch(() => {})
    return () => { alive = false }
  }, [symbol])

  useEffect(() => {
    if (symbolData?.orderbook?.symbol === symbol) setBook(symbolData.orderbook)
  }, [symbolData, symbol])

  const maxB = Math.max(...book.bids.slice(0, 12).map((b) => b[1]), 0.0001)
  const maxA = Math.max(...book.asks.slice(0, 12).map((b) => b[1]), 0.0001)

  const Row = ({ p, a, max, side }: { p: number; a: number; max: number; side: 'b' | 'a' }) => (
    <button onClick={() => onPick(p)} className="relative flex w-full items-center justify-between px-3 py-[5px] text-[11px] transition hover:bg-white/[.05]">
      <span
        className="absolute inset-y-0 right-0"
        style={{
          width: `${Math.min((a / max) * 100, 100)}%`,
          background: side === 'b' ? 'rgba(0,227,154,.09)' : 'rgba(255,77,109,.09)',
        }}
      />
      <span className={clsx('num relative font-medium', side === 'b' ? 'text-up' : 'text-down')}>{num(p)}</span>
      <span className="num relative text-slate-400">{num(a, 4)}</span>
    </button>
  )

  return (
    <Card className="flex h-full flex-col !p-0">
      <div className="border-b border-white/[.06] px-3 py-2.5 text-xs font-semibold text-slate-300">دفتر سفارشات</div>
      <div className="flex justify-between px-3 py-1.5 text-[10px] text-slate-500">
        <span>قیمت</span><span>مقدار</span>
      </div>
      <div className="flex flex-1 flex-col justify-end">
        {book.asks.slice(0, 12).reverse().map((r, i) => <Row key={'a' + i} p={r[0]} a={r[1]} max={maxA} side="a" />)}
      </div>
      <div className="my-1 flex items-center justify-between border-y border-white/[.06] px-3 py-2">
        <span className={clsx('num text-base font-bold', changeClass(ticker?.change))}>{num(ticker?.last)}</span>
        <span className="text-[10px] text-slate-500">{ticker?.quote === 'IRT' ? 'تومان' : ticker?.quote}</span>
      </div>
      <div className="flex-1">
        {book.bids.slice(0, 12).map((r, i) => <Row key={'b' + i} p={r[0]} a={r[1]} max={maxB} side="b" />)}
      </div>
    </Card>
  )
}

/* ---------------------------------------------------------------- معاملات اخیر */
function RecentTrades({ symbol }: { symbol: string }) {
  const symbolData = useMarket((s) => s.symbolData)
  const [rows, setRows] = useState<any[]>([])
  useEffect(() => {
    let alive = true
    get(`/market/trades/${symbol}?limit=26`).then((r: any) => alive && setRows(r)).catch(() => {})
    return () => { alive = false }
  }, [symbol])
  useEffect(() => {
    if (symbolData?.trades && symbolData.ticker?.symbol === symbol) setRows(symbolData.trades)
  }, [symbolData, symbol])

  return (
    <Card className="flex h-full flex-col !p-0">
      <div className="border-b border-white/[.06] px-3 py-2.5 text-xs font-semibold text-slate-300">معاملات اخیر بازار</div>
      <div className="flex justify-between px-3 py-1.5 text-[10px] text-slate-500">
        <span>قیمت</span><span>مقدار</span><span>زمان</span>
      </div>
      <div className="max-h-[320px] flex-1 overflow-y-auto">
        {rows.map((t, i) => (
          <div key={i} className="flex items-center justify-between px-3 py-[5px] text-[11px]">
            <span className={clsx('num font-medium', t.side === 'buy' ? 'text-up' : 'text-down')}>{num(t.price)}</span>
            <span className="num text-slate-400">{num(t.amount, 4)}</span>
            {/* faTime روی مهر زمانیِ ناقص «—» می‌دهد؛ Intl خام اینجا RangeError
                می‌داد و کل صفحه معامله را می‌بست */}
            <span className="num text-slate-600">{faTime(t.time, true)}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

/* ---------------------------------------------------------------- فرم سفارش */
function OrderForm({ symbol, presetPrice, onDone }: { symbol: string; presetPrice: number; onDone: () => void }) {
  const t = useMarket((s) => s.tickers[symbol])
  const { user } = useAuth()
  const [side, setSide] = useState<'buy' | 'sell'>('buy')
  const [type, setType] = useState<'limit' | 'market' | 'stop'>('limit')
  const [price, setPrice] = useState('')
  const [stop, setStop] = useState('')
  const [amount, setAmount] = useState('')
  const [loading, setLoading] = useState(false)
  const [balances, setBalances] = useState<Record<string, number>>({})

  const loadBalances = () => {
    if (!user) return
    get('/wallet/balances').then((r: any) => {
      const m: Record<string, number> = {}
      r.assets.forEach((a: any) => (m[a.currency] = a.balance))
      setBalances(m)
    }).catch(() => {})
  }
  useEffect(loadBalances, [user])
  useEffect(() => { if (presetPrice) setPrice(String(presetPrice)) }, [presetPrice])
  useEffect(() => { if (t && !price) setPrice(String(Number(t.last.toFixed(t.quote === 'IRT' ? 0 : 4)))) }, [t?.symbol])

  const p = type === 'market' ? t?.last ?? 0 : Number(price) || 0
  const a = Number(amount) || 0
  const total = p * a
  const avail = side === 'buy' ? balances[t?.quote ?? 'IRT'] ?? 0 : balances[t?.base ?? ''] ?? 0

  const setPercent = (percent: number) => {
    if (!t) return
    if (side === 'buy') {
      const usable = (avail * percent) / 100
      setAmount(p ? String(Number((usable / p / 1.0015).toFixed(6))) : '')
    } else {
      setAmount(String(Number(((avail * percent) / 100).toFixed(6))))
    }
  }

  const submit = async () => {
    if (!user) return toast.err('برای ثبت سفارش ابتدا وارد حساب کاربری شوید')
    if (!a) return toast.err('مقدار سفارش را وارد کنید')
    setLoading(true)
    try {
      await post('/orders', {
        symbol, side, type,
        price: type === 'market' ? 0 : p,
        stop_price: Number(stop) || 0,
        amount: a,
      })
      toast.ok(side === 'buy' ? 'سفارش خرید با موفقیت ثبت شد' : 'سفارش فروش با موفقیت ثبت شد')
      setAmount('')
      loadBalances()
      onDone()
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="!p-4">
      <div className="mb-4 grid grid-cols-2 gap-2">
        {(['buy', 'sell'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSide(s)}
            className={clsx(
              'rounded-xl py-2.5 text-sm font-bold transition',
              side === s
                ? s === 'buy' ? 'bg-gradient-to-l from-emerald-500 to-up text-ink-950 shadow-glow-up'
                              : 'bg-gradient-to-l from-rose-500 to-down text-white'
                : 'border border-white/10 bg-white/[.03] text-slate-400 hover:text-slate-200',
            )}
          >
            {s === 'buy' ? `خرید ${t?.faName ?? ''}` : `فروش ${t?.faName ?? ''}`}
          </button>
        ))}
      </div>

      <Tabs
        value={type}
        onChange={(k) => setType(k as any)}
        size="sm"
        className="mb-4"
        items={[{ key: 'limit', label: 'حد (Limit)' }, { key: 'market', label: 'بازار (Market)' }, { key: 'stop', label: 'حد ضرر (Stop)' }]}
      />

      <div className="space-y-3">
        {type === 'stop' && (
          <div>
            <label className="label">قیمت فعال‌سازی</label>
            <NumberInput value={stop} onChange={setStop} suffix={t?.quote === 'IRT' ? 'تومان' : t?.quote} />
          </div>
        )}
        <div>
          <label className="label">قیمت واحد</label>
          <NumberInput
            value={type === 'market' ? '' : price}
            onChange={setPrice}
            disabled={type === 'market'}
            placeholder={type === 'market' ? 'بهترین قیمت بازار' : ''}
            suffix={t?.quote === 'IRT' ? 'تومان' : t?.quote}
          />
        </div>
        <div>
          <label className="label">مقدار</label>
          <NumberInput value={amount} onChange={setAmount} suffix={t?.base} />
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          {[25, 50, 75, 100].map((v) => (
            <button key={v} onClick={() => setPercent(v)}
              className="rounded-lg border border-white/[.07] bg-white/[.03] py-1.5 text-[11px] text-slate-400 transition hover:border-brand-500/40 hover:text-brand-200">
              {fa(v)}٪
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-white/[.06] bg-ink-900/50 p-3 text-xs">
          <div className="mb-1.5 flex justify-between text-slate-500">
            <span>موجودی قابل استفاده</span>
            <span className="num text-slate-300">{num(avail, 4)} {side === 'buy' ? (t?.quote === 'IRT' ? 'تومان' : t?.quote) : t?.base}</span>
          </div>
          <div className="flex justify-between text-slate-500">
            <span>مبلغ کل</span>
            <span className="num font-semibold text-white">{num(total)} {t?.quote === 'IRT' ? 'تومان' : t?.quote}</span>
          </div>
        </div>

        <Button variant={side === 'buy' ? 'up' : 'down'} loading={loading} onClick={submit} className="w-full !py-3">
          {user ? (side === 'buy' ? 'ثبت سفارش خرید' : 'ثبت سفارش فروش') : 'ورود به حساب کاربری'}
        </Button>
        {!user && <Link to="/login" className="btn-ghost w-full">ورود / ثبت‌نام</Link>}
        <p className="text-center text-[11px] text-slate-600">کارمزد سازنده {fa('۰٫۱')}٪ — کارمزد بازارگیر {fa('۰٫۱۵')}٪</p>
      </div>
    </Card>
  )
}

/* ------------------------------------------------------- پنل سفارش (عادی/اهرمی)
   زبانه «اهرمی» فقط وقتی ساخته می‌شود که در تنظیمات روشن باشد و این بازار
   هم مجاز باشد. وقتی خاموش است، صفحه دقیقاً همان چیزی است که قبلاً بود —
   نه زبانه‌ای اضافه می‌شود، نه فضایی اشغال می‌شود. */
function TradePanel({ symbol, presetPrice, onDone }: { symbol: string; presetPrice: number; onDone: () => void }) {
  const { cfg } = useLeverageConfig()
  const [mode, setMode] = useState<'spot' | 'lev'>('spot')
  const on = leverageOn(cfg, symbol)

  // اگر اهرم وسطِ کار خاموش شد (یا کاربر به بازاری رفت که اهرم ندارد)،
  // پنل نباید روی زبانه‌ای بماند که دیگر وجود ندارد.
  useEffect(() => { if (!on && mode === 'lev') setMode('spot') }, [on, mode])

  if (!on || !cfg) return <OrderForm symbol={symbol} presetPrice={presetPrice} onDone={onDone} />

  return (
    <div>
      <div className="mb-2 grid grid-cols-2 gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {([['spot', 'معامله عادی'], ['lev', 'اهرمی']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)}
            className={clsx('rounded-lg py-2 text-xs font-bold transition',
              mode === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white' : 'text-slate-400')}>
            {l}
          </button>
        ))}
      </div>
      {mode === 'spot'
        ? <OrderForm symbol={symbol} presetPrice={presetPrice} onDone={onDone} />
        : <Card className="!p-4"><LeverageForm symbol={symbol} cfg={cfg} presetPrice={presetPrice} onDone={onDone} /></Card>}
    </div>
  )
}

/** جدول موقعیت‌های اهرمی — وقتی خاموش است، هیچ چیزی رندر نمی‌شود */
function LevPositions({ refreshKey }: { refreshKey: number }) {
  const { cfg } = useLeverageConfig()
  // به `enabled` گره نمی‌خورد: خودِ `LeveragePositions` تصمیم می‌گیرد —
  // وقتی اهرم خاموش است ولی کاربر موقعیت باز دارد، باید بتواند ببنددش.
  if (!cfg) return null
  return <div className="mt-3"><LeveragePositions cfg={cfg} refreshKey={refreshKey} /></div>
}

/* ---------------------------------------------------------------- سفارش‌های من */
function MyOrders({ symbol, refreshKey }: { symbol: string; refreshKey: number }) {
  const { user } = useAuth()
  const [tab, setTab] = useState('open')
  const [rows, setRows] = useState<any[] | null>(null)

  const load = () => {
    if (!user) return setRows([])
    const url = tab === 'trades' ? `/orders/trades?limit=40` : `/orders?status=${tab}&limit=40`
    get(url).then((r: any) => setRows(asArray(r))).catch(() => setRows([]))
  }
  useEffect(load, [tab, user, refreshKey])

  const cancel = async (id: number) => {
    try {
      await del(`/orders/${id}`)
      toast.ok('سفارش لغو شد')
      load()
    } catch (e: any) { toast.err(e.message) }
  }

  return (
    <Card className="!p-0">
      <div className="border-b border-white/[.06] p-3">
        <Tabs value={tab} onChange={setTab} size="sm"
          items={[{ key: 'open', label: 'سفارش‌های باز' }, { key: 'filled', label: 'تکمیل‌شده' }, { key: 'trades', label: 'معاملات من' }]} />
      </div>
      <div className="overflow-x-auto p-2">
        {!user ? (
          <Empty text="برای مشاهده سفارش‌ها وارد حساب کاربری شوید" icon={<Wallet className="h-6 w-6" />} />
        ) : rows === null ? (
          <div className="p-3"><Rows n={3} /></div>
        ) : rows.length === 0 ? (
          <Empty text="سفارشی برای نمایش وجود ندارد" />
        ) : (
          <>
          {/* موبایل: هر سفارش یک کارت، با دکمه لغو در دسترس شست */}
          <div className="space-y-2 md:hidden">
            {rows.map((o) => (
              <div key={o.id} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className={clsx('text-xs font-bold',
                    o.side === 'buy' ? 'text-up' : o.side === 'sell' ? 'text-down' : 'text-brand-200')}>
                    {o.side === 'buy' ? 'خرید' : o.side === 'sell' ? 'فروش' : 'تبدیل'}
                  </span>
                  <span className="num text-xs text-slate-200" dir="ltr">{o.symbol}</span>
                  <span className="mr-auto">{o.status ? statusBadge(o.status) : statusBadge('filled')}</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
                  <span className="num">قیمت {num(o.price ?? o.avgPrice)}</span>
                  <span className="num">مقدار {num(o.amount, 5)}</span>
                  <span className="text-slate-500">{faDate(o.createdAt)}</span>
                </div>
                {['open', 'partial'].includes(o.status) && (
                  <button onClick={() => cancel(o.id)}
                    className="mt-2 w-full rounded-lg border border-down/30 py-2 text-[11px] text-down active:bg-down/10">
                    لغو سفارش
                  </button>
                )}
              </div>
            ))}
          </div>

          <table className="hidden w-full min-w-[640px] text-xs md:table">
            <thead>
              <tr className="text-[11px] text-slate-500">
                <th className="px-3 py-2 text-right font-medium">بازار</th>
                <th className="px-3 py-2 text-right font-medium">سمت</th>
                <th className="px-3 py-2 text-right font-medium">قیمت</th>
                <th className="px-3 py-2 text-right font-medium">مقدار</th>
                <th className="px-3 py-2 text-right font-medium">وضعیت</th>
                <th className="px-3 py-2 text-right font-medium">زمان</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-white/[.05]">
                  <td className="num px-3 py-2.5 text-slate-200" dir="ltr">{o.symbol}</td>
                  <td className={clsx('px-3 py-2.5 font-semibold', o.side === 'buy' ? 'text-up' : o.side === 'sell' ? 'text-down' : 'text-brand-200')}>
                    {o.side === 'buy' ? 'خرید' : o.side === 'sell' ? 'فروش' : 'تبدیل'}
                  </td>
                  <td className="num px-3 py-2.5 text-slate-300">{num(o.price ?? o.avgPrice)}</td>
                  <td className="num px-3 py-2.5 text-slate-300">{num(o.amount, 5)}</td>
                  <td className="px-3 py-2.5">{o.status ? statusBadge(o.status) : statusBadge('filled')}</td>
                  <td className="px-3 py-2.5 text-slate-500">{faDate(o.createdAt)}</td>
                  <td className="px-3 py-2.5 text-left">
                    {['open', 'partial'].includes(o.status) && (
                      <button onClick={() => cancel(o.id)} className="rounded-lg p-1.5 text-slate-500 transition hover:bg-down/10 hover:text-down">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>
    </Card>
  )
}

/* ---------------------------------------------------------------- صفحه */
export default function Trade() {
  const { symbol = 'BTCIRT' } = useParams()
  const sym = symbol.toUpperCase()
  const t = useMarket((s) => s.tickers[sym])
  const subscribe = useMarket((s) => s.subscribe)
  const [pick, setPick] = useState(0)
  const [refreshKey, setRefreshKey] = useState(0)
  // تب موبایل: نمودار، دفتر سفارش و معاملات اخیر روی گوشی زیر هم چیده
  // می‌شدند و کاربر باید چند صفحه اسکرول می‌کرد تا به فرم سفارش برسد.
  const [mtab, setMtab] = useState<'chart' | 'book' | 'trades'>('chart')
  // فقط یکی از دو چیدمان ساخته می‌شود؛ با پنهان‌کردنِ CSSی، هر دو نسخه
  // نمودار و دفتر سفارش هم‌زمان زنده می‌ماندند و باتری گوشی را می‌خوردند.
  const wide = useIsDesktop()

  useEffect(() => { subscribe(sym) }, [sym, subscribe])

  const stats = useMemo(
    () => [
      ['تغییر ۲۴ ساعت', t ? pct(t.change) : '—', changeClass(t?.change)],
      ['بیشترین', num(t?.high), 'text-slate-200'],
      ['کمترین', num(t?.low), 'text-slate-200'],
      ['حجم', num(t?.volume, 2), 'text-slate-200'],
    ],
    [t],
  )

  return (
    <Shell full>
      <div className="mx-auto max-w-[1600px] px-3 py-4 sm:px-5">
        {/* هدر بازار — روی گوشی، آمارها به‌جای شکستن در چند سطر، افقی می‌لغزند */}
        <Card className="mb-3 !p-3">
          <div className="mb-2 flex items-center justify-between gap-3 xl:mb-0 xl:hidden">
            <MarketPicker symbol={sym} />
            <div className="num text-xl font-extrabold text-white xs:text-2xl">{num(t?.last)}</div>
          </div>
          <div className="scroll-x gap-5 xl:hidden">
            {stats.map(([label, value, cls]) => (
              <div key={label as string} className="shrink-0">
                <div className="text-[10px] text-slate-500">{label}</div>
                <div className={clsx('num text-xs font-semibold', cls as string)}>{value}</div>
              </div>
            ))}
          </div>

          <div className="hidden flex-wrap items-center gap-x-8 gap-y-3 xl:flex">
            <MarketPicker symbol={sym} />
            <div className="num text-2xl font-extrabold text-white">{num(t?.last)}</div>
            {stats.map(([label, value, cls]) => (
              <div key={label as string} className="min-w-[80px]">
                <div className="text-[11px] text-slate-500">{label}</div>
                <div className={clsx('num text-sm font-semibold', cls as string)}>{value}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── موبایل: فرم سفارش اول، بقیه پشت تب.
            کاربر موبایل برای «خرید» آمده؛ نباید سه صفحه اسکرول کند تا به
            دکمه برسد. */}
        {!wide && (
        <div>
          <div className="mb-3">
            <TradePanel symbol={sym} presetPrice={pick} onDone={() => setRefreshKey((k) => k + 1)} />
          </div>
          <div className="mb-2 flex gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
            {([['chart', 'نمودار'], ['book', 'دفتر سفارش'], ['trades', 'معاملات اخیر']] as const).map(([k, l]) => (
              <button key={k} onClick={() => setMtab(k)}
                className={clsx('flex-1 rounded-lg py-2.5 text-xs font-medium transition',
                  mtab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white' : 'text-slate-400')}>
                {l}
              </button>
            ))}
          </div>
          {/* ارتفاع نسبت به صفحه، نه عدد ثابت.
              روی گوشی‌های کوچک ۴۴۰ پیکسل بیشترِ صفحه را می‌گرفت و روی
              گوشی‌های بلند، نمودار بی‌جهت کوچک می‌ماند. کف ۴۲۰ پیکسل هست تا
              با نوار نماها و نوار ابزار، جای خودِ نمودار له نشود. */}
          {mtab === 'chart' && (
            <div className="h-[64vh] min-h-[min(420px,78vh)] max-h-[680px]">
              <ProChart symbol={sym} quote={t?.quote ?? 'IRT'} />
            </div>
          )}
          {mtab === 'book' && <OrderBook symbol={sym} onPick={setPick} />}
          {mtab === 'trades' && <RecentTrades symbol={sym} />}
          <div className="mt-3"><MyOrders symbol={sym} refreshKey={refreshKey} /></div>
          <LevPositions refreshKey={refreshKey} />
        </div>
        )}

        {wide && (<>
        <div className="grid gap-3 xl:grid-cols-[1fr_300px_320px]">
          <div className="order-1 h-[420px] xl:h-[540px]">
            <ProChart symbol={sym} quote={t?.quote ?? 'IRT'} />
          </div>
          <div className="order-3 xl:order-2 xl:h-[540px]">
            <OrderBook symbol={sym} onPick={setPick} />
          </div>
          <div className="order-2 xl:order-3">
            <TradePanel symbol={sym} presetPrice={pick} onDone={() => setRefreshKey((k) => k + 1)} />
          </div>
        </div>

        <div className="mt-3 grid gap-3 xl:grid-cols-[1fr_320px]">
          <MyOrders symbol={sym} refreshKey={refreshKey} />
          <div className="xl:h-auto"><RecentTrades symbol={sym} /></div>
        </div>

        <LevPositions refreshKey={refreshKey} />
        </>)}
      </div>
    </Shell>
  )
}
